import type { PrimeVueLocaleOptions } from 'primevue/config'

/**
 * PrimeVue ships its own English strings for everything the app never writes
 * itself: month and day names, paginator controls, password strength, empty
 * lists. Without this file a Spanish console still says "First Page" and
 * "Enter a password" — English leaking through the parts of the UI nobody
 * remembers to translate, because they are not in our message catalogue.
 *
 * Only the keys the components in use actually read are listed. PrimeVue
 * merges this over its defaults, so anything absent stays English rather than
 * breaking — which is why a key that IS used must not be forgotten here.
 */
export const primeVueEs: Partial<PrimeVueLocaleOptions> = {
  accept: 'Sí',
  reject: 'No',
  choose: 'Elegir',
  upload: 'Subir',
  cancel: 'Cancelar',
  clear: 'Limpiar',
  apply: 'Aplicar',
  completed: 'Completado',
  pending: 'Pendiente',

  dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
  dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
  dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
  monthNames: [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ],
  monthNamesShort: [
    'ene',
    'feb',
    'mar',
    'abr',
    'may',
    'jun',
    'jul',
    'ago',
    'sep',
    'oct',
    'nov',
    'dic',
  ],
  today: 'Hoy',
  weekHeader: 'Sem',

  // The date picker renders these itself; they are top-level locale keys, NOT
  // aria ones. Nested under `aria` they type-check as unknown properties and,
  // worse, would be dead translations the picker never reads.
  chooseDate: 'Elija una fecha',
  chooseMonth: 'Elija un mes',
  chooseYear: 'Elija un año',
  prevMonth: 'Mes anterior',
  nextMonth: 'Mes siguiente',
  prevYear: 'Año anterior',
  nextYear: 'Año siguiente',
  prevDecade: 'Década anterior',
  nextDecade: 'Década siguiente',
  // Sunday. The collection week in the Dominican Republic starts there, and
  // the calendar has to line up with the route sheets people already use.
  firstDayOfWeek: 0,

  weak: 'Débil',
  medium: 'Media',
  strong: 'Fuerte',
  passwordPrompt: 'Ingrese una contraseña',

  emptyMessage: 'No hay opciones disponibles',
  emptyFilterMessage: 'No se encontraron resultados',
  emptySearchMessage: 'No se encontraron resultados',
  emptySelectionMessage: 'No hay ningún elemento seleccionado',
  searchMessage: 'Hay {0} resultados disponibles',
  selectionMessage: '{0} elementos seleccionados',

  aria: {
    close: 'Cerrar',
    selectAll: 'Seleccionar todo',
    unselectAll: 'Quitar la selección de todo',
    selectRow: 'Seleccionar fila',
    unselectRow: 'Quitar la selección de la fila',
    expandRow: 'Expandir fila',
    collapseRow: 'Contraer fila',
    // The paginator renders these as icon-only buttons, so for anyone on a
    // screen reader this text is the ONLY thing naming the control.
    firstPageLabel: 'Primera página',
    prevPageLabel: 'Página anterior',
    nextPageLabel: 'Página siguiente',
    lastPageLabel: 'Última página',
    pageLabel: 'Página {page}',
    rowsPerPageLabel: 'Filas por página',
    jumpToPageDropdownLabel: 'Ir a la página',
    jumpToPageInputLabel: 'Ir a la página',
    navigation: 'Navegación',
    previous: 'Anterior',
    next: 'Siguiente',
    trueLabel: 'Sí',
    falseLabel: 'No',
    nullLabel: 'Sin seleccionar',
    listLabel: 'Lista de opciones',
    star: '1 estrella',
    stars: '{star} estrellas',
  },
}
